import { useEffect, useState, useCallback, useMemo } from 'react'
import useSWR from 'swr'
import { fetcher, junipKey } from './ProductReviews.queries'
import { useReviewFilters, ratings, sizes } from './useReviewFilters'

import ReactStars from 'react-rating-stars-component'
import { roundHalf, relativeDate } from 'lib/helpers'
import { Button } from 'components/Global/Button'
import { ResponsiveImage } from 'components/Global/ResponsiveImage'
import { ProductReviewFilter } from './ProductReviewFilter'
import { ReviewImageOverlay } from './ReviewImageOverlay'

export const ProductReviews = ({
  title,
  average,
  count,
  id,
  productId,
  image,
}) => {
  const [reviews, setReviews] = useState<any>([])
  const [after, setAfter] = useState<string | null>(null)
  const { filters, setFilter } = useReviewFilters()
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null)
  const [expandedImage, setExpandedImage] = useState<string | null>(null)

  const queryString = useMemo(() => {
    let string = ''
    if (filters.rating) {
      string += `&filter[rating]=${filters.rating}`
    }
    if (filters.usualSize) {
      string += `&filter[survey][733]=${filters.usualSize.toUpperCase()}`
    }
    if (filters.purchasedSize) {
      string += `&filter[survey][734]=${filters.purchasedSize.toUpperCase()}`
    }
    if (after) {
      string += `&page[after]=${after}`
    }
    return string
  }, [filters, after])

  const { data, error, isLoading } = useSWR(
    `https://api.juniphq.com/v1/product_reviews?filter[product_remote_ids][]=${id}&page[size]=2&include=tik_tok_urls,product,customer,respondent,survey_answers,tik_tok_urls,trait_ratings&sort[order]=desc&sort[field]=created_at&filter[media_only]=true${queryString}`,
    fetcher
  )

  useMemo(() => {
    if (data) {
      setReviews((prev) => [...prev, ...data.product_reviews])
    }
  }, [data])

  const loadReviews = useCallback(() => {
    setAfter(data.meta.page.after)
  }, [data])

  const updateFilters = useCallback(
    (filter, value) => {
      setAfter(null)
      setReviews([])
      setFilter(filter, value)
    },
    [setFilter]
  )

  return (
    <div className="p-10 md:p-20" id="reviews">
      <ReviewImageOverlay
        image={expandedImage}
        onClose={() => setExpandedImage(null)}
      />
      <div
        className={`flex w-full flex-wrap rounded-8 bg-white p-20 shadow-md ${
          reviews.length >= 1 ? 'min-h-[80vh]' : ''
        }`}
      >
        <div className="w-full md:w-[30%]">
          <div className="text-16 leading-110">
            <h2>Customer Reviews</h2>
            <div className="text-midGray">{title}</div>
          </div>
          <div className="mb-24 flex items-center gap-12 pt-20 text-12 text-midGray">
            <ReactStars
              count={5}
              size={16}
              value={roundHalf(average)}
              color="rgba(94, 94, 94, 0.3)"
              activeColor="rgba(94, 94, 94, 1)"
              edit={false}
            />
            <div>{count} Reviews</div>
          </div>
          <Button
            as="link"
            target="_blank"
            rel="noreferrer noopener"
            href={`https://junip.co/forms/review/onsite/product?store_key=${junipKey}&product_id=${productId}`}
            className="mb-40 w-full bg-yellow py-20 text-center text-14 md:mb-0 md:w-[180px] md:py-10 md:text-11"
          >
            Write a review
          </Button>
          <div
            className="relative mt-14 hidden overflow-hidden rounded-6 md:block md:w-[180px]"
            style={{
              aspectRatio: image?.width / image?.height,
            }}
          >
            <ResponsiveImage src={image?.src} alt={image?.alt} />
          </div>
        </div>
        {reviews.length >= 1 ? (
          <div className="w-full text-darkGray md:flex-1">
            <div className="flex items-center justify-between gap-10 pb-10 text-12 uppercase leading-100 text-midGray md:justify-start md:gap-28">
              <ProductReviewFilter
                label="Rating"
                values={ratings}
                filter="rating"
                setFilter={updateFilters}
                activeFilter={filters['rating']}
                open={expandedFilter === 'rating'}
                setOpen={setExpandedFilter}
              />
              <ProductReviewFilter
                label="Usual Size"
                values={sizes}
                filter="usualSize"
                setFilter={updateFilters}
                activeFilter={filters['usualSize']}
                open={expandedFilter === 'usualSize'}
                setOpen={setExpandedFilter}
              />
              <ProductReviewFilter
                label="Size Purchased"
                values={sizes}
                filter="purchasedSize"
                setFilter={updateFilters}
                activeFilter={filters['purchasedSize']}
                open={expandedFilter === 'purchasedSize'}
                setOpen={setExpandedFilter}
              />
            </div>
            <div className="min-h-[300px]">
              {reviews?.map((review, index) => {
                return (
                  <div
                    key={review.id}
                    className={`w-full border-t-1 border-[#DEDEDE] pb-32 pt-20 ${
                      (data?.meta?.page?.after &&
                        index === reviews.length - 1) ||
                      isLoading
                        ? 'border-b-1 border-[#DEDEDE]'
                        : ''
                    }`}
                  >
                    <div className="flex w-full flex-wrap md:flex-nowrap">
                      <div className="flex w-full justify-between uppercase md:block md:max-w-[250px]">
                        <div className="pb-12">
                          {review.customer?.first_name}{' '}
                          {review.customer?.last_name}.
                        </div>
                        <ReactStars
                          count={5}
                          size={16}
                          value={roundHalf(review.rating)}
                          color="rgba(94, 94, 94, 0.3)"
                          activeColor="rgba(94, 94, 94, 1)"
                          edit={false}
                        />
                      </div>
                      <div className="w-full">
                        <div className="flex w-full justify-between pb-24 uppercase">
                          <div>{review.title}</div>
                          <div className="md:blocks hidden text-10 lowercase text-midGray">
                            {relativeDate(review.created_at)}
                          </div>
                        </div>
                        <p className="max-w-[650px] pb-32">{review.body}</p>
                        {review.survey_answers ? (
                          <div className="pb-40 md:pb-12">
                            {review.survey_answers?.map((answer, i) => {
                              if (answer.name === 'Overall Fit') return null
                              return (
                                <div key={`answer-${i}`} className="flex">
                                  <div className="w-[200px]">{answer.name}</div>
                                  <div>{answer.value}</div>
                                </div>
                              )
                            })}
                          </div>
                        ) : null}
                        {review.photo_urls ? (
                          <div className="flex gap-6">
                            {review.photo_urls.map((url) => {
                              return (
                                <button
                                  key={url.thumbnail}
                                  className="h-[120px]"
                                  onClick={() => setExpandedImage(url)}
                                >
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    className="h-full w-auto"
                                    src={url.thumbnail}
                                    alt=""
                                  />
                                </button>
                              )
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {data?.meta?.page?.after || isLoading ? (
              <Button
                onClick={loadReviews}
                className="background-blur mb-30 mt-12 md:hover:bg-yellow"
              >
                Load more reviews
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
